import styled, { css } from 'styled-components';

export const ModalSubtitle = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
  line-height: 1.45;
`;

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px 22px;
  border: 1px solid #eef2f7;
  border-radius: 12px;
  background: #ffffff;

  & + & {
    margin-top: 14px;
  }
`;

export const SectionHead = styled.header`
  display: flex;
  align-items: center;
  gap: 10px;

  .badge {
    width: 32px;
    height: 32px;
    border-radius: 10px;
    background: #e6f3ec;
    color: #006633;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  h4 {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.01em;
  }

  .subtitle {
    display: block;
    margin-top: 2px;
    font-size: 12px;
    color: #64748b;
    font-weight: 500;
  }
`;

export const Grid = styled.div<{ $cols?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $cols = 2 }) => $cols}, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

export const FieldBlock = styled.div<{ $span?: number }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  grid-column: span ${({ $span = 1 }) => $span};

  @media (max-width: 720px) {
    grid-column: span 1;
  }
`;

export const Label = styled.label`
  margin: 0;
  font-size: 11.5px;
  font-weight: 700;
  color: #475569;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const inputBase = css`
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13.5px;
  color: #0f172a;
  background: #ffffff;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    outline: none;
    border-color: #006633;
    box-shadow: 0 0 0 3px rgba(0, 102, 51, 0.12);
  }

  &:disabled {
    background: #f8fafc;
    color: #94a3b8;
    cursor: not-allowed;
  }
`;

export const TextInput = styled.input`
  ${inputBase};
`;

export const Select = styled.select`
  ${inputBase};
  appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 34px;
  cursor: pointer;
`;

export const ToggleGroup = styled.div`
  display: inline-flex;
  gap: 8px;
`;

export const ToggleChip = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 0 18px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s ease;

  ${({ $active }) =>
    $active
      ? css`
          border: 1px solid #006633;
          background: #f1faf4;
          color: #006633;
          box-shadow: 0 0 0 3px rgba(0, 102, 51, 0.1);
        `
      : css`
          border: 1px solid #e2e8f0;
          background: #ffffff;
          color: #475569;

          &:hover {
            border-color: #c7e4d5;
            background: #fafbfc;
          }
        `}

  .marker {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 2px solid ${({ $active }) => ($active ? '#006633' : '#cbd5e1')};
    background: ${({ $active }) => ($active ? '#006633' : 'transparent')};
    position: relative;

    ${({ $active }) =>
      $active &&
      css`
        &::after {
          content: '';
          position: absolute;
          inset: 2px;
          border-radius: 50%;
          background: #ffffff;
        }
      `}
  }
`;

export const OriginAddressBlock = styled.div`
  margin-top: 6px;
  padding: 16px;
  background: #fafbfc;
  border: 1px dashed #e2e8f0;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  .block-title {
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
`;

export const FooterHint = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 14px;
  padding: 10px 12px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
  border-radius: 10px;
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  line-height: 1.45;

  svg {
    flex-shrink: 0;
    margin-top: 1px;
    color: #94a3b8;
  }
`;

export const FooterButton = styled.button<{
  $variant?: 'primary' | 'secondary' | 'danger';
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 38px;
  padding: 0 18px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s ease;

  ${({ $variant = 'primary' }) => {
    if ($variant === 'danger') {
      return css`
        background: #b91c1c;
        color: #ffffff;
        border: 1px solid #b91c1c;

        &:hover:not(:disabled) {
          background: #991b1b;
        }
      `;
    }
    if ($variant === 'secondary') {
      return css`
        background: #ffffff;
        color: #0f172a;
        border: 1px solid #e2e8f0;

        &:hover:not(:disabled) {
          background: #f8fafc;
          border-color: #006633;
          color: #006633;
        }
      `;
    }
    return css`
      background: #006633;
      color: #ffffff;
      border: 1px solid #006633;

      &:hover:not(:disabled) {
        background: #00451f;
      }
    `;
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
